import { elfLogger } from './elf-logger.service';

let handlersInstalled = false;

/**
 * Installs global browser error capture (uncaught errors + unhandled promise
 * rejections) that forwards to ELF. Mirrors the intent of one-ap-customer_web's
 * global ElfErrorHandler in a React app, which has no framework-level
 * ErrorHandler hook. Idempotent and strictly best-effort.
 */
export const installElfGlobalErrorHandlers = (): void => {
  if (handlersInstalled || typeof window === 'undefined') {
    return;
  }
  handlersInstalled = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    try {
      const error = event.error != null ? event.error : event.message;
      elfLogger.logException(error, {
        'error.source': 'window.error',
        'error.filename': event.filename || undefined,
        'error.lineno': typeof event.lineno === 'number' ? event.lineno : undefined,
        'error.colno': typeof event.colno === 'number' ? event.colno : undefined
      });
    } catch {
      // The error handler must never throw.
    }
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    try {
      elfLogger.logException(event.reason, { 'error.source': 'window.unhandledrejection' });
    } catch {
      // The error handler must never throw.
    }
  });
};

/**
 * Forward a React error-boundary catch to ELF. Call from componentDidCatch so
 * render-time errors are captured with their component stack.
 */
export const logComponentError = (error: unknown, info?: { componentStack?: string }): void => {
  try {
    elfLogger.logException(error, {
      'error.source': 'ReactErrorBoundary',
      'react.component_stack': info && info.componentStack ? info.componentStack : undefined
    });
  } catch {
    // The error handler must never throw.
  }
};
